import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, User, Save, Loader2, Play, Trash2, Camera, Watch } from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';
import { Pencil } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useComprehensiveNutrition } from '@/contexts/NutritionContext';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Capacitor } from '@capacitor/core';
import { useTranslation } from 'react-i18next';
import {
  disconnectAppleHealth,
  getHealthConnectedState,
  presentHealthScreenNative,
} from '@/lib/appleHealth';
import { Analytics } from '@/lib/analytics';
import { LanguageSettings } from '@/components/settings/LanguageSettings';
import { UnitsSettings } from '@/components/settings/UnitsSettings';
import {
  type UnitSystem,
  getStoredUnits,
  setStoredUnits,
  resolveUnitSystem,
  displayWeightToStoredKg,
  kgToDisplayWeight,
  displayHeightToCm,
  cmToDisplayHeight,
  weightUnitLabel,
  parseHeightToCm,
  parseWeightToKg,
  roundMetricForProfile,
} from '@/lib/units';
import { usePremium } from '@/hooks/usePremium';
import { PremiumPaywall } from '@/components/PremiumPaywall';
import { restorePremiumAndSync } from '@/lib/premiumPurchase';
import { isPurchasesAvailable } from '@/lib/straivPurchases';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

export const Settings = () => {
  const navigate = useNavigate();
  const { profile, updateProfile } = useProfile();
  const { isPremium, refetchProfile } = usePremium();
  const { user, signOut } = useAuth();
  const { t } = useTranslation();
  const { recalculateGoalsIfNeeded, nutritionGoals } = useComprehensiveNutrition();
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [restoringPurchases, setRestoringPurchases] = useState(false);
  const [healthConnected, setHealthConnected] = useState<boolean | null>(null);
  const [isDisconnectingHealth, setIsDisconnectingHealth] = useState(false);
  const [isReconnectingHealth, setIsReconnectingHealth] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDeleteAccount = async () => {
    if (!user) return;
    
    setIsDeleting(true);
    try {
      // Call edge function to delete user account (including from auth.users)
      const { data, error } = await supabase.functions.invoke('delete-user-account');

      if (error) {
        console.error('Error deleting account:', error);
        toast.error(t('settings.deleteAccount.error'));
        return;
      }

      // Sign out the user locally
      await signOut();
      
      Analytics.accountDeleted();
      toast.success(t('settings.deleteAccount.success'));
      navigate('/auth', { replace: true });
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error(t('settings.deleteAccount.error'));
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    fitness_level: '',
    primary_goals: [] as string[],
    experience_years: 0,
    height_cm: '',
    height_ft: '',
    height_in: '',
    weight_kg: '',
    units: 'metric' as UnitSystem,
    birth_date: '',
    gender: '',
    activity_level: '',
    ai_coach_name: 'Arnold'
  });

  const isIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

  useEffect(() => {
    if (isIOS && user?.id) getHealthConnectedState(user.id).then(setHealthConnected);
  }, [isIOS, user?.id]);

  const handleDisconnectAppleHealth = async () => {
    if (!user?.id || !isIOS) return;
    setIsDisconnectingHealth(true);
    try {
      await disconnectAppleHealth(user.id);
      setHealthConnected(false);
      toast.success(t('settings.appleHealth.disconnectSuccess'));
    } catch {
      toast.error(t('settings.appleHealth.disconnectError'));
    } finally {
      setIsDisconnectingHealth(false);
    }
  };

  const handleReconnectAppleHealth = async () => {
    if (!user?.id || !isIOS) return;
    setIsReconnectingHealth(true);
    try {
      const ok = await presentHealthScreenNative({
        syncOnDismiss: true,
        userId: user.id,
        skipFeedback: true,
      });
      if (ok) {
        // Re-poll the connected state - presentHealthScreenNative resolves
        // true once the user grants permission and the first sync runs.
        const connected = await getHealthConnectedState(user.id);
        setHealthConnected(connected);
        if (connected) toast.success(t('settings.appleHealth.reconnectSuccess'));
      }
    } catch (err) {
      console.error('[Settings] Apple Health reconnect failed:', err);
      toast.error(t('settings.appleHealth.reconnectFailed'));
    } finally {
      setIsReconnectingHealth(false);
    }
  };

  // Update form data when profile loads or changes
  useEffect(() => {
    if (profile) {
      const units = resolveUnitSystem(profile.units ?? getStoredUnits());
      const heightCm = profile.height_cm ?? null;
      const weightKg = profile.weight_kg ?? null;
      let heightDisplay = heightCm?.toString() || '';
      let heightFt = '';
      let heightIn = '';
      let weightDisplay = weightKg?.toString() || '';
      if (heightCm != null) {
        const { ft = 0, inch = 0 } = cmToDisplayHeight(heightCm, 'imperial');
        heightFt = String(ft);
        heightIn = String(inch);
        if (units === 'imperial') {
          heightDisplay = `${ft}'${inch}"`;
        }
      }
      if (units === 'imperial' && weightKg != null) {
        weightDisplay = String(kgToDisplayWeight(weightKg, 'imperial'));
      }
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        fitness_level: profile.fitness_level || '',
        primary_goals: profile.primary_goals || [],
        experience_years: profile.experience_years || 0,
        height_cm: heightDisplay,
        height_ft: heightFt,
        height_in: heightIn,
        weight_kg: weightDisplay,
        units,
        birth_date: profile.birth_date || '',
        gender: profile.gender || '',
        activity_level: profile.activity_level || '',
        ai_coach_name: profile.ai_coach_name || 'Arnold'
      });
    }
  }, [profile]);

  const fitnessLevels = [
    { value: 'beginner', label: t('settings.fitnessLevels.beginner') },
    { value: 'intermediate', label: t('settings.fitnessLevels.intermediate') },
    { value: 'advanced', label: t('settings.fitnessLevels.advanced') },
    { value: 'elite', label: t('settings.fitnessLevels.elite') }
  ];

  const activityLevels = [
    { value: 'sedentary', label: t('settings.activityLevels.sedentary') },
    { value: 'light', label: t('settings.activityLevels.light') },
    { value: 'moderate', label: t('settings.activityLevels.moderate') },
    { value: 'active', label: t('settings.activityLevels.active') },
    { value: 'extreme', label: t('settings.activityLevels.extreme') }
  ];

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const units = formData.units || 'metric';
      const heightCm =
        units === 'imperial'
          ? roundMetricForProfile(
              (() => {
                const ft = parseInt(formData.height_ft || '', 10);
                const inch = parseInt(formData.height_in || '0', 10);
                if (!Number.isFinite(ft) || formData.height_ft === '') return null;
                if (!Number.isFinite(inch) || inch < 0 || inch >= 12) return null;
                return Math.round((ft * 12 + inch) * 2.54);
              })(),
            )
          : formData.height_cm
            ? roundMetricForProfile(parseHeightToCm(formData.height_cm, 'metric'))
            : null;
      const weightKg = formData.weight_kg
        ? parseWeightToKg(formData.weight_kg, units)
        : null;
      if ((formData.height_cm || formData.height_ft) && heightCm == null) {
        toast.error(
          units === 'imperial'
            ? t('onboarding.validation.validHeightImperial', {
                defaultValue: 'Please enter a valid height in feet and inches',
              })
            : t('onboarding.validation.validHeight'),
        );
        setIsLoading(false);
        return;
      }
      if (formData.weight_kg && weightKg == null) {
        toast.error(
          units === 'imperial'
            ? t('onboarding.validation.validWeightImperial', {
                defaultValue: 'Please enter a valid weight in pounds',
              })
            : t('onboarding.validation.validWeight'),
        );
        setIsLoading(false);
        return;
      }
      setStoredUnits(units);
      const updates = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        fitness_level: formData.fitness_level,
        primary_goals: formData.primary_goals,
        experience_years: formData.experience_years,
        birth_date: formData.birth_date,
        gender: formData.gender,
        activity_level: formData.activity_level,
        ai_coach_name: formData.ai_coach_name,
        height_cm: heightCm,
        weight_kg: weightKg,
        units,
      };

      const { error } = await updateProfile(updates as any);
      
      if (error) {
        toast.error(t('settings.save.profileError'));
        return;
      }

      Analytics.profileUpdated();
      Analytics.settingsChanged('profile');

      // Check if user has manual overrides - if so, don't auto-recalculate
      if (nutritionGoals?.is_manual_override) {
        toast.success(t('settings.save.successManualGoals'));
      } else {
        // Recalculate nutrition goals based on updated profile
        try {
          await recalculateGoalsIfNeeded(true); // Force recalculation
          toast.success(t('settings.save.successRecalculated'));
        } catch (goalError) {
          console.error('Error recalculating goals:', goalError);
          toast.success(t('settings.save.successFallback'));
        }
      }

      navigate(-1); // Go back to previous page
    } catch (error) {
      toast.error(t('settings.save.genericError'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!profile) {
    return (
      <div className="bg-gradient-bg p-4 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-primary mb-2">{t('settings.loading.title')}</h2>
          <p className="text-muted-foreground">{t('settings.loading.description')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-bg p-4 pb-24">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl font-normal">{t('settings.pageTitle')}</h1>
            <p className="text-muted-foreground">{t('settings.pageSubtitle')}</p>
          </div>
        </div>

        {/* Profile Picture */}
        <Card className="bg-gradient-card border-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5 text-primary" />
              {t('settings.profilePicture.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4">
            <div 
              className="relative w-24 h-24 rounded-full overflow-hidden cursor-pointer group"
              onClick={() => fileInputRef.current?.click()}
            >
              {profile?.avatar_url ? (
                <img 
                  src={profile.avatar_url} 
                  alt={t('settings.profilePicture.avatarAlt')} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-primary flex items-center justify-center text-black font-bold text-2xl">
                  {profile?.first_name?.[0]?.toUpperCase() || 'U'}
                </div>
              )}
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                {isUploadingAvatar ? (
                  <Loader2 className="h-6 w-6 text-white animate-spin" />
                ) : (
                  <Camera className="h-6 w-6 text-white" />
                )}
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file || !user) return;
                
                setIsUploadingAvatar(true);
                try {
                  const fileExt = file.name.split('.').pop();
                  const filePath = `${user.id}/avatar.${fileExt}`;
                  
                  const { error: uploadError } = await supabase.storage
                    .from('avatars')
                    .upload(filePath, file, { upsert: true });
                  
                  if (uploadError) throw uploadError;
                  
                  const { data: { publicUrl } } = supabase.storage
                    .from('avatars')
                    .getPublicUrl(filePath);
                  
                  const avatarUrlWithCacheBust = `${publicUrl}?t=${Date.now()}`;
                  await updateProfile({ avatar_url: avatarUrlWithCacheBust });
                  toast.success(t('settings.avatar.success'));
                } catch (error) {
                  console.error('Error uploading avatar:', error);
                  toast.error(t('settings.avatar.error'));
                } finally {
                  setIsUploadingAvatar(false);
                  e.target.value = '';
                }
              }}
            />
            <p className="text-xs text-muted-foreground">{t('settings.profilePicture.hint')}</p>
          </CardContent>
        </Card>

        {/* Basic Information */}
        <Card className="bg-gradient-card border-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              {t('settings.basicInfo.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">{t('settings.basicInfo.firstName')}</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder={t('settings.basicInfo.firstNamePlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">{t('settings.basicInfo.lastName')}</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder={t('settings.basicInfo.lastNamePlaceholder')}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="birth_date">{t('settings.basicInfo.birthDate')}</Label>
                <Input
                  id="birth_date"
                  type="date"
                  value={formData.birth_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, birth_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gender">{t('settings.basicInfo.gender')}</Label>
                <select
                  id="gender"
                  value={formData.gender}
                  onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value }))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">{t('settings.basicInfo.genderPlaceholder')}</option>
                  <option value="male">{t('settings.gender.male')}</option>
                  <option value="female">{t('settings.gender.female')}</option>
                  <option value="other">{t('settings.gender.other')}</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>
                  {formData.units === 'imperial'
                    ? t('settings.basicInfo.heightFtIn', { defaultValue: 'Height' })
                    : t('settings.basicInfo.height')}
                </Label>
                {formData.units === 'imperial' ? (
                  <div className="grid grid-cols-2 gap-2">
                    <div className="relative">
                      <Input
                        id="height_ft"
                        type="number"
                        inputMode="numeric"
                        min={3}
                        max={8}
                        value={formData.height_ft}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^\d]/g, '');
                          setFormData((prev) => ({ ...prev, height_ft: v }));
                        }}
                        placeholder="5"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">ft</span>
                    </div>
                    <div className="relative">
                      <Input
                        id="height_in"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={11}
                        value={formData.height_in}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^\d]/g, '');
                          const n = parseInt(v || '0', 10);
                          setFormData((prev) => ({
                            ...prev,
                            height_in: v === '' ? '' : String(Math.min(11, Math.max(0, n))),
                          }));
                        }}
                        placeholder="10"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">in</span>
                    </div>
                  </div>
                ) : (
                  <Input
                    id="height_cm"
                    type="text"
                    inputMode="decimal"
                    value={formData.height_cm}
                    onChange={(e) => setFormData((prev) => ({ ...prev, height_cm: e.target.value }))}
                    placeholder="170"
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="weight_kg">
                  {formData.units === 'imperial'
                    ? t('settings.basicInfo.weightLb', { defaultValue: 'Weight (lb)' })
                    : t('settings.basicInfo.weight')}
                </Label>
                <Input
                  id="weight_kg"
                  type="text"
                  inputMode="decimal"
                  value={formData.weight_kg}
                  onChange={(e) => {
                    const value = e.target.value.replace(',', '.');
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      setFormData(prev => ({ ...prev, weight_kg: value }));
                    }
                  }}
                  placeholder={formData.units === 'imperial' ? '165' : '70'}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Fitness Information */}
        <Card className="bg-gradient-card border-0">
          <CardHeader>
            <CardTitle>{t('settings.fitness.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fitness_level">{t('settings.fitness.levelLabel')}</Label>
                <select
                  id="fitness_level"
                  value={formData.fitness_level}
                  onChange={(e) => setFormData(prev => ({ ...prev, fitness_level: e.target.value }))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">{t('settings.fitness.levelPlaceholder')}</option>
                  {fitnessLevels.map(level => (
                    <option key={level.value} value={level.value}>{level.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="experience_years">{t('settings.fitness.experienceLabel')}</Label>
                <Input
                  id="experience_years"
                  type="number"
                  min="0"
                  max="50"
                  value={formData.experience_years}
                  onChange={(e) => setFormData(prev => ({ ...prev, experience_years: parseInt(e.target.value) || 0 }))}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="activity_level">{t('settings.fitness.activityLabel')}</Label>
              <select
                id="activity_level"
                value={formData.activity_level}
                onChange={(e) => setFormData(prev => ({ ...prev, activity_level: e.target.value }))}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">{t('settings.fitness.activityPlaceholder')}</option>
                {activityLevels.map(level => (
                  <option key={level.value} value={level.value}>{level.label}</option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Language */}
        <LanguageSettings />

        {/* Units (below language) — metric km/kg or imperial mi/lb */}
        <UnitsSettings
          value={formData.units}
          onChange={(units) => {
            setFormData((prev) => {
              const from = prev.units;
              let height_cm = prev.height_cm;
              let height_ft = prev.height_ft;
              let height_in = prev.height_in;
              let weight_kg = prev.weight_kg;
              if (from !== units) {
                if (units === 'imperial' && from === 'metric') {
                  const cm = parseFloat(prev.height_cm);
                  const kg = parseFloat(prev.weight_kg);
                  if (Number.isFinite(cm)) {
                    const { ft = 0, inch = 0 } = cmToDisplayHeight(cm, 'imperial');
                    height_ft = String(ft);
                    height_in = String(inch);
                    height_cm = `${ft}'${inch}"`;
                  }
                  if (Number.isFinite(kg)) {
                    weight_kg = String(kgToDisplayWeight(kg, 'imperial'));
                  }
                } else if (units === 'metric' && from === 'imperial') {
                  const ft = parseInt(prev.height_ft || '', 10);
                  const inch = parseInt(prev.height_in || '0', 10);
                  if (Number.isFinite(ft)) {
                    height_cm = String(displayHeightToCm(ft, Number.isFinite(inch) ? inch : 0));
                  }
                  const lb = parseFloat(prev.weight_kg);
                  if (Number.isFinite(lb)) {
                    weight_kg = String(displayWeightToStoredKg(lb, 'imperial') ?? '');
                  }
                }
              }
              setStoredUnits(units);
              return { ...prev, units, height_cm, height_ft, height_in, weight_kg };
            });
          }}
        />

        <Card className="bg-gradient-card border-0">
          <CardHeader>
            <CardTitle>{t('premium.settingsSectionTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              {isPremium ? t('premium.settingsPremiumActive') : t('premium.settingsFree')}
            </p>
            {!isPremium && <p>{t('premium.comingSoonBody')}</p>}
            {!isPremium && (
              <Button
                type="button"
                className="w-full bg-gradient-primary text-black font-semibold"
                onClick={() => setShowPaywall(true)}
              >
                {t('premium.settingsManage')}
              </Button>
            )}
            {isPurchasesAvailable() && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                disabled={restoringPurchases}
                onClick={async () => {
                  setRestoringPurchases(true);
                  try {
                    const result = await restorePremiumAndSync();
                    if (result.premium) {
                      Analytics.purchaseRestored(true, 'settings');
                      Analytics.setPremiumStatus(true);
                      await refetchProfile();
                      toast.success(t('premium.paywallRestored'));
                    } else {
                      Analytics.restoreEmpty('settings');
                      toast.message(result.message || t('premium.paywallNothingToRestore'));
                    }
                  } catch (err: any) {
                    Analytics.purchaseFailed('restore', err?.message || 'restore_error', 'settings');
                    toast.error(err?.message || t('premium.paywallRestoreFailed'));
                  } finally {
                    setRestoringPurchases(false);
                  }
                }}
              >
                {restoringPurchases ? <Loader2 className="h-4 w-4 animate-spin" /> : t('premium.settingsRestore')}
              </Button>
            )}
            {(import.meta.env.DEV || import.meta.env.VITE_PREMIUM_PREVIEW === '1') && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => {
                  const on = localStorage.getItem('straiv_premium_preview') === '1';
                  if (on) localStorage.removeItem('straiv_premium_preview');
                  else localStorage.setItem('straiv_premium_preview', '1');
                  toast.success(on ? t('premium.settingsPreviewOff') : t('premium.settingsPreviewOn'));
                  window.location.reload();
                }}
              >
                {t('premium.settingsPreviewToggle')}
              </Button>
            )}
          </CardContent>
        </Card>

        <PremiumPaywall open={showPaywall} onOpenChange={setShowPaywall} source="settings" />

        {/* AI Coach Name */}
        <Card className="bg-gradient-card border-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-primary" />
              {t('settings.aiCoach.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="ai_coach_name">{t('settings.aiCoach.fieldLabel')}</Label>
              <Input
                id="ai_coach_name"
                value={formData.ai_coach_name}
                onChange={(e) => setFormData(prev => ({ ...prev, ai_coach_name: e.target.value }))}
                placeholder={t('settings.aiCoach.placeholder')}
              />
              <p className="text-xs text-muted-foreground">
                {t('settings.aiCoach.help')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Disconnect Apple Health - iOS only */}
        {isIOS && (
          <Card className="bg-gradient-card border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Watch className="h-5 w-5 text-primary" />
                {t('settings.appleHealthCardTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  {healthConnected === null
                    ? t('settings.appleHealth.checking')
                    : healthConnected
                    ? t('settings.appleHealth.connectedDescription')
                    : t('settings.appleHealth.disconnectedDescription')}
                </p>
                {healthConnected === true && (
                  <Button
                    variant="outline"
                    onClick={handleDisconnectAppleHealth}
                    disabled={isDisconnectingHealth}
                    className="border-destructive/30 text-destructive hover:bg-destructive/10"
                  >
                    {isDisconnectingHealth ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('settings.appleHealth.disconnecting')}
                      </>
                    ) : (
                      <>
                        <Watch className="mr-2 h-4 w-4" />
                        {t('settings.appleHealth.disconnect')}
                      </>
                    )}
                  </Button>
                )}
                {healthConnected === false && (
                  <Button
                    onClick={handleReconnectAppleHealth}
                    disabled={isReconnectingHealth}
                    className="bg-gradient-primary text-black shadow-glow hover:shadow-lg transition-all duration-300"
                  >
                    {isReconnectingHealth ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('settings.appleHealth.reconnecting')}
                      </>
                    ) : (
                      <>
                        <Watch className="mr-2 h-4 w-4" />
                        {t('settings.appleHealth.reconnect')}
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Save Button */}
        <div className="flex gap-3">
          <Button
            onClick={() => {
              try {
                // Immediately force tutorial to show again via localStorage + navigation
                localStorage.setItem('tutorial_completed', 'false');
                localStorage.setItem('force_tutorial', 'true');
                updateProfile({ tutorial_completed: false });
                toast.success(t('settings.tutorial.restartSuccess'));
                navigate('/dashboard', { replace: true });
              } catch (error) {
                console.error('Error resetting tutorial:', error);
                toast.error(t('settings.tutorial.restartError'));
              }
            }}
            variant="outline"
            className="flex-1 h-12"
          >
            <Play className="mr-2 h-4 w-4" />
            {t('settings.tutorial.viewButton')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading}
            className="flex-1 h-12 bg-gradient-primary text-black shadow-glow hover:shadow-lg transition-all duration-300"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('settings.save.saving')}
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {t('settings.save.button')}
              </>
            )}
          </Button>
        </div>

        {/* Delete Account */}
        <div className="pt-4 flex justify-center">
          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive hover:bg-destructive/10">
                <Trash2 className="h-3 w-3 mr-1" />
                {t('settings.deleteAccount.trigger')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('settings.deleteAccount.confirmTitle')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('settings.deleteAccount.confirmIntro')}
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>{t('settings.deleteAccount.listProfile')}</li>
                    <li>{t('settings.deleteAccount.listWorkouts')}</li>
                    <li>{t('settings.deleteAccount.listNutrition')}</li>
                    <li>{t('settings.deleteAccount.listGoals')}</li>
                    <li>{t('settings.deleteAccount.listChat')}</li>
                  </ul>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDeleting}>{t('common.cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteAccount}
                  disabled={isDeleting}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('settings.deleteAccount.deleting')}
                    </>
                  ) : (
                    t('settings.deleteAccount.confirmButton')
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
};